<script setup lang="ts">
defineOptions({ inheritAttrs: false });

const model = defineModel<boolean>({ default: false });

defineProps<{
  /** Accessible name when no label slot text is enough on its own. */
  ariaLabel?: string;
  disabled?: boolean;
  id?: string;
}>();
</script>

<template>
  <label class="zw-checkbox" :class="{ 'zw-checkbox-disabled': disabled }">
    <input
      :id="id"
      v-model="model"
      class="zw-checkbox-input"
      type="checkbox"
      :disabled="disabled"
      :aria-label="ariaLabel"
      v-bind="$attrs"
    />
    <span class="zw-checkbox-box" aria-hidden="true">
      <svg
        class="zw-checkbox-mark"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3.5 8.2 6.6 11.2 12.5 4.8"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
    <span v-if="typeof $slots.default === 'function'" class="zw-checkbox-label">
      <slot />
    </span>
  </label>
</template>

<style>
.zw-checkbox {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-family: var(--font-ui);
  font-size: 0.95rem;
  letter-spacing: 0;
  text-transform: none;
  color: var(--text);
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
}

.zw-checkbox-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.zw-checkbox-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.zw-checkbox-box {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 0.95rem;
  height: 0.95rem;
  margin-top: 0.12em;
  display: grid;
  place-items: center;
  border: 1px solid var(--line);
  border-radius: 0.2rem;
  background: var(--bg);
  color: transparent;
  transition:
    background-color var(--duration-fast) var(--ease-out-quart),
    border-color var(--duration-fast) var(--ease-out-quart),
    color var(--duration-fast) var(--ease-out-quart);
}

.zw-checkbox-mark {
  width: 0.72rem;
  height: 0.72rem;
}

.zw-checkbox-input:checked + .zw-checkbox-box {
  background: color-mix(in oklch, var(--color-zw-amber) 22%, var(--bg));
  border-color: color-mix(in oklch, var(--color-zw-amber) 70%, var(--line));
  color: var(--color-zw-amber);
}

.zw-checkbox-input:focus-visible + .zw-checkbox-box {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.zw-checkbox:hover:not(.zw-checkbox-disabled)
  .zw-checkbox-input:not(:checked)
  + .zw-checkbox-box {
  border-color: color-mix(in oklch, var(--color-zw-amber) 45%, var(--line));
}

.zw-checkbox-label {
  line-height: 1.35;
  color: var(--text);
}
</style>
